import { useFormik } from "formik";
import React, { useEffect } from "react";
import { useDispatch } from "react-redux";
import * as Yup from "yup";
import { Button } from "primereact/button";
import { InputText } from "primereact/inputtext";
import { InputTextarea } from "primereact/inputtextarea";
import { Checkbox } from "primereact/checkbox";
import { addWarehouse, updateWarehouse } from "../../store/AsyncMethods/WarehouseMethod";
import { toast } from "react-toastify";

export default function AddWarehouseForm({ warehouse, onSuccess, onCancel }) {
  const dispatch = useDispatch();
  const isEdit = !!warehouse;

  const formik = useFormik({
    initialValues: {
      name: warehouse?.name || "",
      address: warehouse?.address || "",
      phone: warehouse?.phone || "",
      city: warehouse?.city || "",
      is_active: warehouse?.is_active !== undefined ? warehouse.is_active : true,
    },
    validationSchema: Yup.object({
      name: Yup.string().required("Warehouse name is required"),
      address: Yup.string().required("Address is required"),
      phone: Yup.string().required("Phone number is required"),
      city: Yup.string().required("City is required"),
    }),
    onSubmit: (values) => {
      if (isEdit) {
        dispatch(updateWarehouse(values, warehouse.id)).then((success) => {
          if (success) {
            toast.success("Warehouse updated successfully!");
            onSuccess();
          }
        });
      } else {
        dispatch(addWarehouse(values)).then((success) => {
          if (success) {
            toast.success("Warehouse added successfully!");
            onSuccess();
          }
        });
      }
    },
  });

  useEffect(() => {
    if (warehouse) {
      formik.setValues({
        name: warehouse.name || "",
        address: warehouse.address || "",
        phone: warehouse.phone || "",
        city: warehouse.city || "",
        is_active: warehouse.is_active !== undefined ? warehouse.is_active : true,
      });
    }
  }, [warehouse]);

  return (
    <form onSubmit={formik.handleSubmit} className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Warehouse Name */}
        <div className="md:col-span-2">
          <div className="flex flex-col gap-2">
            <label htmlFor="name" className="font-semibold text-gray-700">
              Warehouse Name *
            </label>
            <InputText
              id="name"
              name="name"
              className="w-full"
              value={formik.values.name}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
            />
            {formik.touched.name && formik.errors.name && (
              <div className="text-red-500 text-sm">{formik.errors.name}</div>
            )}
          </div>
        </div>

        {/* City */}
        <div>
          <div className="flex flex-col gap-2">
            <label htmlFor="city" className="font-semibold text-gray-700">
              City *
            </label>
            <InputText
              id="city"
              name="city"
              className="w-full"
              value={formik.values.city}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
            />
            {formik.touched.city && formik.errors.city && (
              <div className="text-red-500 text-sm">{formik.errors.city}</div>
            )}
          </div>
        </div>

        {/* Phone */}
        <div>
          <div className="flex flex-col gap-2">
            <label htmlFor="phone" className="font-semibold text-gray-700">
              Phone Number *
            </label>
            <InputText
              id="phone"
              name="phone"
              className="w-full"
              value={formik.values.phone}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              placeholder="XX-XX-XXXXXXX"
            />
            {formik.touched.phone && formik.errors.phone && (
              <div className="text-red-500 text-sm">{formik.errors.phone}</div>
            )}
          </div>
        </div>

        {/* Address */}
        <div className="md:col-span-2">
          <div className="flex flex-col gap-2">
            <label htmlFor="address" className="font-semibold text-gray-700">
              Complete Address *
            </label>
            <InputTextarea
              id="address"
              name="address"
              className="w-full"
              rows={3}
              value={formik.values.address}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              placeholder="Enter complete warehouse address..."
            />
            {formik.touched.address && formik.errors.address && (
              <div className="text-red-500 text-sm">{formik.errors.address}</div>
            )}
          </div>
        </div>

        {/* Active Status */}
        <div className="md:col-span-2">
          <div className="flex items-center gap-2">
            <Checkbox
              inputId="is_active"
              name="is_active"
              checked={formik.values.is_active}
              onChange={(e) => formik.setFieldValue("is_active", e.checked)}
            />
            <label htmlFor="is_active" className="font-semibold text-gray-700">
              Active (Warehouse is operational)
            </label>
          </div>
        </div>
      </div>

      {/* Form Actions */}
      <div className="flex justify-end gap-4 pt-6 border-t">
        <Button
          label="Cancel"
          icon="pi pi-times"
          className="p-button-text"
          type="button"
          onClick={onCancel}
        />
        <Button
          label={isEdit ? "Update Warehouse" : "Add Warehouse"}
          icon={isEdit ? "pi pi-check" : "pi pi-plus"}
          className="p-primary-btn"
          type="submit"
          disabled={!formik.isValid || formik.isSubmitting}
        />
      </div>
    </form>
  );
}
